// SPDX-FileCopyrightText: 2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** Search entities in the established filter order. */
export const SEARCH_ENTITY_KEYS = [
  "titles",
  "lyrics",
  "translations",
  "chapters",
  "authors",
  "offered_to",
  "extra_info",
  "tags",
  "numbers",
  "ids",
] as const;
export type SearchEntity = (typeof SEARCH_ENTITY_KEYS)[number];
export type SearchUrlSelection = string[] | "all" | null;
/** Null uses all known selections; an empty array explicitly disables all selections. */
export type SearchUrlState = {
  query: string;
  books: SearchUrlSelection;
  variants: SearchUrlSelection;
  entities: SearchUrlSelection;
};
export type ViewUrlState = {
  view: string;
  songId: string | null;
  search: SearchUrlState;
  lang: string | null;
  hash: string;
};
export const SEARCH_PARAM_NAMES = {
  query: "q",
  books: "books",
  variants: "variants",
  entities: "entities",
} as const;
export const SEARCH_PARAM_KEYS = Object.values(SEARCH_PARAM_NAMES);
const searchNames = SEARCH_PARAM_NAMES;
export const MAX_SONG_ID_LENGTH = 256;
export const MAX_SEARCH_QUERY_LENGTH = 512;
export const MAX_SEARCH_SELECTION_ITEMS = 128;
export const MAX_SEARCH_SELECTION_ITEM_LENGTH = 128;
const controls = /[\u0000-\u001f\u007f]/;

/** Parses server-visible search parameters using the established tilde list scheme. */
export function parseSearchUrlState(params: URLSearchParams): SearchUrlState {
  const list = (key: string): SearchUrlSelection => {
    const value = params.get(key);
    if (value === null) return null;
    if (
      value.length > MAX_SEARCH_SELECTION_ITEMS * (MAX_SEARCH_SELECTION_ITEM_LENGTH + 1) ||
      controls.test(value)
    )
      return [];
    const items = value
      .trim()
      .split("~")
      .map(item => item.trim())
      .filter(Boolean);
    if (
      items.length > MAX_SEARCH_SELECTION_ITEMS ||
      items.some(item => item.length > MAX_SEARCH_SELECTION_ITEM_LENGTH)
    )
      return [];
    if (items.length === 1 && items[0].toLowerCase() === "all") return "all";
    if (items.length === 1 && items[0].toLowerCase() === "none") return [];
    return items;
  };
  const query = params.get(searchNames.query) ?? "";
  return {
    query: query.length <= MAX_SEARCH_QUERY_LENGTH && !controls.test(query) ? query : "",
    books: list(searchNames.books),
    variants: list(searchNames.variants),
    entities: list(searchNames.entities),
  };
}

/** Updates only search parameters, preserving unrelated URL state. */
export function writeSearchUrlState(params: URLSearchParams, state: SearchUrlState): void {
  for (const key of Object.keys(searchNames) as (keyof SearchUrlState)[]) {
    const selection = state[key];
    const value = Array.isArray(selection)
      ? selection.length
        ? selection.join("~")
        : "none"
      : selection;
    if (value) params.set(searchNames[key], value);
    else params.delete(searchNames[key]);
  }
}

/** Whether at least one explicit search parameter is present, including an empty query. */
export function hasSearchUrlState(params: URLSearchParams): boolean {
  return Object.values(searchNames).some(key => params.has(key));
}

/** Removes only search state, leaving PDF destinations and other parameters intact. */
export function stripSearchUrlState(params: URLSearchParams): void {
  Object.values(searchNames).forEach(key => params.delete(key));
}

/** Parses server-visible view state; PDF hashes are opaque and never search state. */
export function parseViewUrl(input: string | URL): ViewUrlState {
  const url = new URL(String(input), "https://view-url.invalid/");
  const params = url.searchParams;
  const view = url.searchParams.get("view")?.trim() || "home";
  const songId = params.get("id");
  return {
    view,
    songId: songId && songId.length <= MAX_SONG_ID_LENGTH && !controls.test(songId) ? songId : null,
    search: parseSearchUrlState(params),
    lang: params.get("lang")?.trim() || null,
    hash: url.hash,
  };
}

/** Serializes owned query state deterministically; the default language is omitted. */
export function serializeViewUrl(
  state: {
    view: string;
    songId?: string | null;
    search?: SearchUrlState;
    lang?: string | null;
    hash?: string;
  },
  options: { defaultLanguage?: string } = {},
): string {
  const params = new URLSearchParams();
  const view = state.view.trim() || "home";
  if (view !== "home") params.set("view", view);
  if (view === "song" && state.songId) params.set("id", state.songId);
  if (view === "search" && state.search) writeSearchUrlState(params, state.search);
  const lang = state.lang?.trim();
  if (lang && lang !== (options.defaultLanguage ?? "en")) params.set("lang", lang);
  const hash = state.hash?.replace(/^#/, "") || "";
  const query = params.toString();
  return `/${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}
